import React, { useContext, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from 'react-query';

import ApiContext from 'api/ApiContext';
import BreadcrumbsContext from 'lib/WAPBreadcrumbs/BreadcrumbsContext';
import formatDate from 'utils/formatDate';
import { Pagesection, Contentheader, Loading } from '@ktag/styleguide/dist/react';
import AdditionalResources from 'lib/AdditionalResources/AdditionalResources';
import { useTranslation } from 'react-i18next';
import BackButton from 'lib/BackButton/BackButton';
import PollNavigation from 'lib/PollNavigation/PollNavigation';
import WAPBreadcrumbs from 'lib/WAPBreadcrumbs/WAPBreadcrumbs';
import NotFoundMessage from 'lib/ErrorMessage/NotFoundMessage';
import ProgressBar from 'lib/ProgressBar/ProgressBar';
import { getInternalUrl, getPollIdFromUrlParams } from 'utils/routing';
import { isPoll } from 'utils/poll';
import IssueDetails from 'lib/IssueDetails/IssueDetails';
import useTrackPageView from 'utils/useTrackPageView';

const Poll = () => {
  const { t } = useTranslation('wap');
  const { id, parentDate } = useParams();
  useTrackPageView();
  const pollId = getPollIdFromUrlParams(id, parentDate);

  const { getPoll, language } = useContext(ApiContext);
  const query = useQuery(['poll', pollId], () => getPoll(pollId));
  const { setBreadcrumbItems } = useContext(BreadcrumbsContext);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pollId]);

  const date = query.data?.data.info?.date;
  const parentLabel = formatDate(date, { month: 'long', day: 'numeric' });
  const parentId = query.data?.data.info.polling_day_id;
  const parentFrontendId = query.data?.data.info.polling_day_frontend_id;
  const isNational = query.data?.data.info.scope === 'federal';
  const isFinal = query.data?.data.progress.final;

  const shortLabel = (query.data?.data.info.short_title || query.data?.data.info.title)?.[
    language
  ];

  const countHasStarted =
    query.data?.data.progress.number_of_finished_counting_circles !== 0;

  const leadtext = query.data?.data?.info?.info?.[language] && (
    <span
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: query.data?.data?.info?.info?.[language] }}
    />
  );

  useEffect(() => {
    if (!shortLabel) return;

    const breadcrumbItems = [
      {
        title: formatDate(date, { month: 'long', day: 'numeric' }),
        url: getInternalUrl({ id: parentId, frontend_id: parentFrontendId }, 'poll_day'),
      },
      {
        title: shortLabel,
        url: '#',
      },
    ];

    setBreadcrumbItems(breadcrumbItems);
  }, [shortLabel]);

  if (query.status === 'error') return <NotFoundMessage type="poll" />;

  let progressBarLabel = isFinal
    ? t('result_final_singular')
    : t('result_ongoing_singular');
  if (isNational) progressBarLabel += ` ${t('progress_label_federal_elections')}`;

  let longTitle = query.data?.data.info.title[language];
  if (query.data?.data?.info.majority_election_info?.round_number) {
    longTitle += ` (${t('round', {
      count: query.data?.data?.info.majority_election_info.round_number,
    })})`;
  }

  return (
    <>
      <Pagesection>
        <Contentheader
          additionalClassNames="poll__contentheader"
          beforeBreadcrumbs={<WAPBreadcrumbs />}
          beforeBacklink={
            <BackButton
              label={parentLabel}
              parentId={parentId}
              parentFrontendId={parentFrontendId}
            />
          }
          pagetitle={query.data?.data?.info?.short_title?.[language] || longTitle}
          beforeLeadtext={
            <PollNavigation
              pollId={pollId}
              parentId={parentId}
              backLabel={parentLabel}
              small
            />
          }
          subtitle={query.data?.data?.info?.short_title?.[language] && longTitle}
          subtitleMobileMaxLength={120}
          leadtext={leadtext}
          printing={!query.isLoading}
        />
      </Pagesection>
      <Pagesection additionalClassNames="pagesection__poll">
        {query.isLoading && <Loading size="big" />}
        {query.isError && <p>Error</p>}
        {countHasStarted && !query.isLoading && (
          <h2 className="h3">{progressBarLabel}</h2>
        )}
        <ProgressBar
          issue={query.data?.data}
          showVoterInfos={!isPoll(query.data?.data.info.type)}
        />
      </Pagesection>

      {countHasStarted && <IssueDetails query={query} pollId={pollId} />}

      <Pagesection type="slender">
        <AdditionalResources
          id={query.data?.data.info.id}
          hasDownloads={query.data?.data.info.resources.downloads}
          links={query.data?.data.info.links}
          headingLevel={2}
        />
        <PollNavigation pollId={pollId} parentId={parentId} backLabel={parentLabel} />
      </Pagesection>
    </>
  );
};

export default Poll;
